import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Power, ExternalLink, Radio, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { announcementZonesAPI, gatesAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const ZONE_TYPE_LABELS = { terminal: 'Terminal', bus: 'Bus', all: 'Airport Wide' };
const ZONE_TYPE_TONES = { terminal: 'info', bus: 'orange', all: 'purple' };

// checkbox/checkbox-primary was a leftover DaisyUI class name (never
// installed — see index.css) that left this checkbox completely unstyled.
const CHECKBOX_CLASS = 'h-4 w-4 rounded border-gray-300 accent-blue-600';

const EMPTY_FORM = { zone_type: 'terminal', zone_code: '', zone_name: '', terminal: '', sort_order: '' };

export default function AdminZonesPage() {
  const [zones, setZones] = useState([]);
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedGateIds, setSelectedGateIds] = useState([]);
  const [gateSearch, setGateSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadZones(); loadGates(); }, []);

  async function loadZones() {
    try {
      const res = await announcementZonesAPI.getAllIncludingInactive();
      setZones(res.data.data || []);
    } catch { toast.error('Failed to load zones'); }
    finally { setLoading(false); }
  }

  async function loadGates() {
    try {
      const res = await gatesAPI.getAll();
      setGates(res.data.data || []);
    } catch { /* non-critical — gate picker just stays empty */ }
  }

  // How many gates each zone currently owns — recomputed from the same live
  // gates list the picker uses, so it can never drift from what's actually saved.
  const gateCountByZone = gates.reduce((acc, g) => {
    if (g.zone_id) acc[g.zone_id] = (acc[g.zone_id] || 0) + 1;
    return acc;
  }, {});

  function openCreate() {
    setEditingZone(null);
    setForm(EMPTY_FORM);
    setSelectedGateIds([]);
    setGateSearch('');
    setModalOpen(true);
  }

  function openEdit(zone) {
    setEditingZone(zone);
    setForm({ zone_type: zone.zone_type, zone_code: zone.zone_code, zone_name: zone.zone_name, terminal: zone.terminal || '', sort_order: zone.sort_order });
    setSelectedGateIds(gates.filter(g => g.zone_id === zone.id).map(g => g.id));
    setGateSearch('');
    setModalOpen(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.zone_code.trim() || !form.zone_name.trim()) {
      toast.error('Zone code and name are required');
      return;
    }
    setSaving(true);
    try {
      let zoneId = editingZone?.id;
      if (editingZone) {
        await announcementZonesAPI.update(editingZone.id, {
          zone_name: form.zone_name,
          terminal: form.terminal || null,
          sort_order: parseInt(form.sort_order) || 99,
          is_active: true,
        });
        toast.success('Zone updated');
      } else {
        const res = await announcementZonesAPI.create({
          zone_type: form.zone_type,
          zone_code: form.zone_code,
          zone_name: form.zone_name,
          terminal: form.terminal || null,
          sort_order: parseInt(form.sort_order) || 99,
        });
        zoneId = res.data.data.id;
        toast.success('Zone created');
      }
      // Syncing gates here is all that's needed for supervisor/manager gate
      // visibility, ad-hoc zone restriction, etc. to reflect it — they all read
      // gates.zone_id live, nothing else needs to be told about this change.
      await announcementZonesAPI.setGates(zoneId, selectedGateIds);
      setModalOpen(false);
      loadZones();
      loadGates();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to save zone');
    } finally { setSaving(false); }
  }

  function toggleGate(id) {
    setSelectedGateIds(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  }

  async function handleToggle(zone) {
    try {
      if (zone.is_active) {
        await announcementZonesAPI.deactivate(zone.id);
        toast.success('Zone deactivated');
      } else {
        await announcementZonesAPI.update(zone.id, { is_active: true });
        toast.success('Zone reactivated');
      }
      loadZones();
    } catch { toast.error('Failed to update zone'); }
  }

  const active = zones.filter(z => z.is_active);
  const inactive = zones.filter(z => !z.is_active);

  return (
    <div className="px-4 py-5 max-w-3xl mx-auto">
      <PageHeader
        title="Announcement Zones"
        subtitle="Terminals, buses and other PA locations"
        actions={<Button variant="primary" icon={Plus} onClick={openCreate}>New Zone</Button>}
      />

      {/* Device URL tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5 text-sm text-blue-700">
        <strong>Zone Display URL:</strong> Open <code className="bg-blue-100 px-1 rounded">/zone-display/[ZONE_CODE]</code> on any screen in that area — e.g. <code className="bg-blue-100 px-1 rounded">/zone-display/T1</code> for Terminal 1.
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <Card padded={false} className="overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Zones ({active.length})</h2>
            </div>
            {active.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No active zones</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-2.5 font-semibold text-gray-600 text-xs">Zone</th>
                    <th className="text-left px-5 py-2.5 font-semibold text-gray-600 text-xs">Type</th>
                    <th className="text-left px-5 py-2.5 font-semibold text-gray-600 text-xs">Code</th>
                    <th className="text-left px-5 py-2.5 font-semibold text-gray-600 text-xs">Gates</th>
                    <th className="text-right px-5 py-2.5 font-semibold text-gray-600 text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map(zone => (
                    <tr key={zone.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-5 py-3 font-semibold text-gray-800">{zone.zone_name}</td>
                      <td className="px-5 py-3">
                        <StatusBadge label={ZONE_TYPE_LABELS[zone.zone_type]} tone={ZONE_TYPE_TONES[zone.zone_type]} />
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-gray-500">{zone.zone_code}</td>
                      <td className="px-5 py-3">
                        {gateCountByZone[zone.id] > 0 ? (
                          <span className="flex items-center gap-1 text-xs text-gray-600">
                            <Radio size={11} className="text-gray-400" />
                            {gateCountByZone[zone.id]} gate{gateCountByZone[zone.id] > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-orange-500">No gates assigned</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <a href={`/zone-display/${zone.zone_code}`} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors" title="Open display">
                            <ExternalLink size={14} />
                          </a>
                          <button onClick={() => openEdit(zone)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleToggle(zone)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors" title="Deactivate">
                            <Power size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {inactive.length > 0 && (
            <Card padded={false} className="overflow-hidden opacity-60">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Inactive ({inactive.length})</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {inactive.map(zone => (
                    <tr key={zone.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-5 py-3 text-gray-500">{zone.zone_name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-gray-400">{zone.zone_code}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => handleToggle(zone)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors" title="Reactivate">
                          <Power size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editingZone ? 'Edit Zone' : 'New Zone'}</h2>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {!editingZone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <div className="flex gap-2">
                    {Object.entries(ZONE_TYPE_LABELS).map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setForm(f => ({ ...f, zone_type: v }))}
                        className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold transition-all ${form.zone_type === v ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-100 text-gray-500'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!editingZone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zone Code *</label>
                  <input value={form.zone_code} onChange={e => setForm(f => ({ ...f, zone_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. T4, BUS_T3_T4" required
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                  <p className="text-xs text-gray-400 mt-1">Used in the URL: /zone-display/T4</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input value={form.zone_name} onChange={e => setForm(f => ({ ...f, zone_name: e.target.value }))}
                  placeholder="e.g. Terminal 4, Bus T3 ↔ T4" required
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {form.zone_type === 'terminal' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terminal (for grouping)</label>
                  <input value={form.terminal} onChange={e => setForm(f => ({ ...f, terminal: e.target.value }))}
                    placeholder="e.g. T4"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                  placeholder="99"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Gates in this zone — this mapping is what supervisor/manager gate
                  visibility and ad-hoc zone restriction both read live, so it syncs
                  automatically the moment it's saved, nothing else to configure. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Radio size={13} className="inline mr-1 text-gray-400" />
                  Gates in this zone
                  <span className="text-xs text-gray-400 font-normal ml-1">({selectedGateIds.length} selected)</span>
                </label>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={gateSearch}
                        onChange={e => setGateSearch(e.target.value)}
                        placeholder="Search gates…"
                        className="w-full pl-7 pr-3 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {gates
                      .filter(g =>
                        !gateSearch ||
                        g.gate_code.toLowerCase().includes(gateSearch.toLowerCase()) ||
                        (g.terminal || '').toLowerCase().includes(gateSearch.toLowerCase())
                      )
                      .map(g => {
                        const belongsElsewhere = g.zone_id && g.zone_id !== editingZone?.id;
                        return (
                          <label key={g.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              className={CHECKBOX_CLASS}
                              checked={selectedGateIds.includes(g.id)}
                              onChange={() => toggleGate(g.id)}
                            />
                            <span className="text-sm text-gray-800 font-medium">{g.gate_code}</span>
                            {g.terminal && <span className="text-xs text-gray-500">{g.terminal}</span>}
                            {belongsElsewhere && (
                              <span className="text-xs text-orange-500 ml-auto">
                                in {zones.find(z => z.id === g.zone_id)?.zone_name || 'another zone'}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    {gates.length === 0 && (
                      <p className="px-3 py-3 text-sm text-gray-400 text-center">No gates available</p>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">A gate can only belong to one zone — selecting it here removes it from any other.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={saving} className="flex-1">
                  {saving ? 'Saving…' : editingZone ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
